import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useSelector , useIntl, history } from 'umi';
import { useRequest } from 'ahooks';
import { Bubble, Button, Card, Table, TableColumn, Text, ConfigProvider, Checkbox } from 'tea-component';
import classnames from 'classnames';

import { DATALAB_EDITOR, SYSTEM_NAME } from '@dbl/core/global';
import { getLocalText } from '@/pages/datalab/utils/utils';
import BubbleEllipsis from '@/pages/datalab/components/BubbleEllipsis';
import { AuthPermissionDisablesContainers } from '../../../../WorkBench/MyDashboard';

import { LANGUAGE_EN } from '@home/_config/config';
import { getPersonalDataList } from "@/pages/datalab/pages/Number/PersonalData/service";
import PersonalDataDetail from "../PersonalDataDetail";
import DeleteModal from "../DeleteModal";
import { IPersonalDataTableRef, IPersonalDataTableProps, TSortBy, TPageInfo } from './types';
import './index.scss';
const { pageable, scrollable, sortable, autotip } = Table.addons;

const PersonalDataTable = forwardRef<IPersonalDataTableRef, IPersonalDataTableProps>(({
  searchWords,
  selectedKeys, setSelectedKeys,
}, ref) => {
  const { gameCode } = useSelector((state: any) => ({
    gameCode: state.datalab.gameCode,
  }));

  const { locale, formatMessage } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  // 内部状态管理
  const [records, setRecords] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [pageInfo, setPageInfo] = useState<TPageInfo>({ pageSize: 10, pageIndex: 1 });
  const [sort, setSort] = useState<TSortBy>({ by: 'create_time_str', order: 'desc' });
  
  // Details 弹窗相关状态
  const [showTableDetail, setShowTableDetail] = useState<boolean>(false);
  const [tableDetailId, setTableDetailId] = useState<string>('');
  const [tableDetailName, setTableDetailName] = useState<string>('');
  
  // 删除弹窗信息
  const [deleteInfo, setDeleteInfo] = useState({ visible: false, record: {} });

  const { loading, error, runAsync } = useRequest(getPersonalDataList, { manual: true });

  const requestDataTable = (nextPageInfo: TPageInfo, nextSort: TSortBy) => {
    setRecords([]);
    setPageInfo(nextPageInfo);
    setSort(nextSort ?? {});

    runAsync({
      game_code: gameCode,
      keywords: searchWords,
      sorts: [
        {
          value: nextSort?.order,
          key: nextSort?.by,
        }
      ],
      page: nextPageInfo.pageIndex,
      page_size: nextPageInfo.pageSize,
    })
      .then((res: any) => {
        setRecords(res?.data_list || []);
        setTotal(res?.row_count || 0);
      })
      .catch(() => {});
  };

  // 直接在 useImperativeHandle 中实现刷新方法
  useImperativeHandle(ref, () => ({
    refreshTable: () => {
      const newPageInfo = { pageSize: 10, pageIndex: 1 };
      const newSort: TSortBy = { by: 'create_time_str', order: 'desc' };
      requestDataTable(newPageInfo, newSort);
    },
  }), []);

  // 监听searchWords变化，重新请求数据
  useEffect(() => {
    const newPageInfo = { pageSize: 10, pageIndex: 1 };
    const newSort: TSortBy = { by: 'create_time_str', order: 'desc' };
    requestDataTable(newPageInfo, newSort);
  }, [searchWords]);

  // 处理分页变化
  const handlePagingChange = (pagingQuery: any) => {
    requestDataTable(pagingQuery as any, sort);
  };

  // 处理排序变化
  const handleSortChange = (sortValues: any[]) => {
    requestDataTable(pageInfo, sortValues?.[0]);
  };

  const columns: TableColumn[] = [
    {
      key: 'table_name',
      header: formatMessage({ id: 'dbl_personal_data_name' }),
      width: 400,
      fixed: 'left',
      render: (record: any) => {
        const isInclude = selectedKeys?.some((tableId) => tableId === record.table_id);
        const name = getLocalText(isEn, record?.customize_name, record?.customize_name_en, record?.table_name);
        const disabled = selectedKeys.length >= 3 && !isInclude;
        return (<>
          <Bubble
            dark
            content={disabled ? formatMessage({ id: 'dbl_personal_data_select_limit_tip' }) : undefined}
            placement={'top'}
          >
            <Checkbox
              value={isInclude}
              disabled={disabled}
              style={{ marginRight: '10px', paddingLeft: '16px' }}
              onChange={(value: boolean) => {
                setSelectedKeys((selectKeys: string[]) => {
                  
                  if (!value) {
                    return selectKeys?.filter((tableId) => tableId !== record.table_id) || selectKeys;
                  }
                  
                  return [...selectKeys,record.table_id];
                });
              }}
            />
          </Bubble>
          
          <Bubble
            dark
            content={name}
            placement={'top'}
          >
            <Text style={{ maxWidth: 335 }} overflow>
              {name}
            </Text>
          </Bubble>
        </>);
      },
    },
    {
      key: 'table_desc',
      header: formatMessage({ id: 'dbl_personal_data_description' }),
      width: 350,
      render: (record: any) => {
        const desc = isEn ? record.table_desc_en || record.table_desc : record.table_desc;
        return desc ? (
          <BubbleEllipsis content={desc} className={'data-management-table-desc-bubble'}>
            <Text style={{ width: '100%' }} overflow>
              {desc}
            </Text>
          </BubbleEllipsis>
        ) : (
          '-'
        );
      },
    },
    {
      key: 'create_time_str',
      header: formatMessage({ id: 'dbl_personal_data_creation_time' }),
      width: 190,
    },
    {
      key: 'handle',
      header: formatMessage({ id: 'dbl_personal_data_operation' }),
      width: isEn ? 200 : 150,
      fixed: 'right',
      render: (record: any) => {
        const { read, delete: deletePermission, table_id, customize_name, customize_name_en, table_name } = record || {};
        
        return (<>
          <AuthPermissionDisablesContainers isShow type={DATALAB_EDITOR.AddChartBtn}>
            <Button
              className="dbl-personal-data-table-analysis-btn"
              type="link"
              onClick={() => {
                history.push(`/${SYSTEM_NAME}/${gameCode}/Analysis/MultiAnalysis?tableId=${record.table_id}`);
              }}
            >
              {formatMessage({ id: 'dbl_personal_data_analysis' })}
            </Button>
          </AuthPermissionDisablesContainers>
          {
            read && <Button
              className="dbl-personal-data-table-analysis-btn"
              type="link"
              onClick={() => {
                const name = getLocalText(isEn, customize_name, customize_name_en, table_name);
                setTableDetailId(table_id);
                setTableDetailName(name);
                setShowTableDetail(true);
              }}
            >
              {formatMessage({ id: 'dbl_personal_data_details' })}
            </Button>
          }
          {
            deletePermission && (<Button
              className="dbl-personal-data-table-analysis-btn"
              type="link"
              onClick={() => {
                setDeleteInfo({ visible: true, record });
              }}
            >
              {formatMessage({id: 'dbl_common_delete'})}
            </Button>)
          }
        </>
        );
      },
    },
  ];

  return (
    <>
      <Card>
        <Card.Body>
          <ConfigProvider locale={isEn ? 'en' : 'zh'}>
            <Table
              recordKey="table_id"
              className={classnames('dbl-personal-data-table')}
              columns={columns}
              records={records}
              addons={[
                scrollable({ minWidth: 1036 }),
                pageable({
                  isPagingReset: true,
                  pageSize: pageInfo.pageSize,
                  pageIndex: pageInfo.pageIndex,
                  pageSizeOptions: [10, 20, 50, 100],
                  recordCount: total,
                  stateText: formatMessage({ id: 'dbl_personal_data_total' }, { total }),
                  stateTextVisible: total > 10,
                  pageSizeVisible: total > 10,
                  pageIndexVisible: total > 10,
                  jumpVisible: total > 10,
                  endJumpVisible: total > 10,
                  onPagingChange: handlePagingChange,
                }),
                sortable({
                  columns: [
                    { key: 'table_name', prefer: 'asc' },
                    { key: 'create_time_str', prefer: 'desc' },
                  ],
                  value: [sort as any],
                  onChange: handleSortChange,
                }),
                autotip({
                  isLoading: loading,
                  loadingText: formatMessage({ id: 'dbl_common_loading' }),
                  errorText: formatMessage({ id: 'dbl_common_load_fail' }),
                  isError: !!error,
                  isFound: Boolean(searchWords) && total === 0,
                  foundText: formatMessage({ id: 'dbl_common_not_search' }),
                  style: { lineHeight: '200px' },
                }),
              ]}
            />
          </ConfigProvider>
        </Card.Body>
      </Card>
      
      {/* Details 弹窗 */}
      <PersonalDataDetail
        showTableDetail={showTableDetail}
        setShowTableDetail={setShowTableDetail}
        tableDetailId={tableDetailId}
        tableName={tableDetailName}
        gameCode={gameCode}
      />
      
      <DeleteModal
        gameCode={gameCode}
        deleteInfo={deleteInfo}
        setDeleteInfo={setDeleteInfo}
        deleteCallBack={() => {
          
          const isReset = pageInfo?.pageIndex > 1 && total - 1 === (pageInfo?.pageIndex - 1) * pageInfo?.pageSize;
          requestDataTable(isReset ? { ...pageInfo, pageIndex: 1 } : pageInfo, sort);
        }}
      />
    </>
  );
});

export default PersonalDataTable;
